import { Link } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { HiArrowRight, HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import hero1 from "../../assets/homehero1.png";
import hero2 from "../../assets/homehero2.png";

const slides = [
  {
    id: 1,
    eyebrow: "Admissions Open",
    title: "Schooling That Fits Your Life",
    image: hero1,
    cta: { label: "Apply Now", to: "/contact" },
  },
  {
    id: 2,
    eyebrow: "Globally Recognised. Universally Accepted",
    title: "Your Future Starts Here",
    image: hero2,
    cta: { label: "Start Your Journey", to: "/admission-process" },
  },
];

const SWIPE_MIN_PX = 56;
const AUTO_ADVANCE_MS = 6000;

function HomeHero() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentSlide = slides[currentIndex];
  const touchStartRef = useRef(null);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (slides.length <= 1) return;
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prevSlide, nextSlide, slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reducedMotion.matches) return;
    const id = window.setInterval(nextSlide, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [nextSlide, slides.length]);

  const onTouchStart = useCallback((e) => {
    if (slides.length <= 1) return;
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  }, []);

  const onTouchEnd = useCallback(
    (e) => {
      if (slides.length <= 1) return;
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy)) return;
      if (dx > 0) prevSlide();
      else nextSlide();
    },
    [prevSlide, nextSlide]
  );

  return (
    <section
      className="relative isolate min-h-[72vh] w-full overflow-hidden sm:min-h-[82vh] lg:min-h-[84vh]"
      aria-roledescription="carousel"
      aria-label="Featured highlights"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Background Image - key changes here */}
      <div className="absolute inset-0 transition-all duration-500 ease-out">
        <img
          src={currentSlide.image}
          alt={currentSlide.title}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-brand-dark/40" />
      </div>

      <div className="relative mx-auto flex min-h-[72vh] max-w-7xl items-center px-4 pb-12 pt-10 sm:min-h-[82vh] sm:px-8 sm:pb-20 sm:pt-20 md:px-10 lg:min-h-[84vh] lg:px-8 lg:pb-16 lg:pt-20">
        <div
          key={currentSlide.id}
          className="w-full max-w-xl animate-[heroIn_0.5s_ease-out_both] p-2 sm:p-4"
        >
          {currentSlide.eyebrow ? (
            <p className="text-sm font-medium uppercase tracking-[0.14em] text-amber-200 sm:text-base">
              {currentSlide.eyebrow}
            </p>
          ) : null}
          <h1 className="mt-3 font-serif text-balance text-5xl font-bold leading-[1.05] tracking-tight text-white drop-shadow-[0_4px_18px_rgba(0,0,0,0.45)] sm:text-6xl md:text-7xl">
            {currentSlide.title}
          </h1>
          <div className="mt-8">
            <Link
              to={currentSlide.cta.to}
              className="group inline-flex items-center justify-center gap-2 rounded-lg border border-white/70 bg-white/10 px-5 py-3 text-center text-base font-semibold text-white backdrop-blur-sm transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:px-6"
            >
              {currentSlide.cta.label}
              <HiArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation Arrows — hidden below md; swipe + dots on mobile */}
      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={prevSlide}
            className="absolute left-4 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white shadow-lg backdrop-blur-sm transition hover:bg-black/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white md:flex"
            aria-label="Previous slide"
          >
            <HiChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={nextSlide}
            className="absolute right-4 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white shadow-lg backdrop-blur-sm transition hover:bg-black/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white md:flex"
            aria-label="Next slide"
          >
            <HiChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 gap-3">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentIndex(index)}
              className={`rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? "h-3 w-8 bg-soft-gold"
                  : "h-3 w-3 bg-white/60 hover:bg-white/90"
              }`}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentIndex ? "true" : undefined}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes heroIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}

export default HomeHero;